import Echo from 'laravel-echo';
import Pusher from 'pusher-js';
import { GLOBAL, EVENTS } from '@/app/shared/utils/constants';

export default {
    data() {
        return {
            broadcasting: {
                echo: null,
                subscribedChannels: {
                    public: [],
                    private: [],
                }
            }
        }
    },
    computed: {
        broadcastingActive() {
            return !!this.$env?.BROADCASTING;
        },
        currentUser: {
            cache: false,
            get(){ return this.$auth.user() }
        },
        currentUserId: {
            cache: false,
            get(){ return this.currentUser ? this.currentUser.id : null }
        },
        authorizationToken: {
            cache: false,
            get(){ return this.$auth.token() }
        },
        notifications: {
            cache: false,
            get(){ return this.$store.getters.notifications.reverse() }
        },
        isConnected: {
            cache: false,
            get(){
                return (this.broadcasting.echo && this.broadcasting.echo.connector.pusher.connection.connection !== null)
            }
        },
        channels: {
            cache: false,
            get(){
                let channels = {};
                channels[`public`] = {
                    '.message': (payload) => {
                        this.$eventBus.publish(GLOBAL.MODULE, EVENTS.SHOW_MESSAGE, {'title': payload.title ?? 'broadcasting.title', ...payload});
                    },
                };
                channels[`private:message.${ this.currentUserId }`] = {
                    '.message': (payload) => {
                        this.$eventBus.publish(GLOBAL.MODULE, EVENTS.SHOW_MESSAGE, {'title': payload.title ?? 'broadcasting.title', ...payload});
                    },
                };
                channels[`private:notification.${ this.currentUserId }`] = {
                    '.message': (payload) => {
                        this.$eventBus.publish(GLOBAL.MODULE, EVENTS.SHOW_NOTIFICATION, {'title': payload.title ?? 'broadcasting.title', ...payload});
                    },
                };
                return channels;
            }
        }
    },
    mounted(){
        this.connect();
    },
    beforeDestroy(){
        this.disconnect();
    },
    watch: {
        currentUser: {
            handler(currentUser){
                this.setAuthorization();
                (currentUser !== null ? this.bindChannels('private') : this.disconnectChannels('private'))
            }
        },
        isConnected: {
            handler(isConnected){
                this.$emit('broadcasting:status', isConnected)
            }
        }
    },
    methods: {
        /** Connect Echo **/
        connect(){
            if(!this.broadcastingActive) return;

            try {
                if(!this.broadcasting.echo){
                    let config = this.$root.$env;
                    let configEcho = config?.PUSHER || {};

                    this.broadcasting.echo = new Echo({
                        broadcaster: 'pusher',
                        key: configEcho.KEY,
                        cluster: configEcho.CLUSTER ?? 'mt1',
                        wsHost: configEcho.HOST, // `${config.APP_URL}`,    //window.location.hostname
                        wsPort: configEcho.PORT ?? 80,
                        wssPort: configEcho.PORT_HTTPS ?? 443,
                        wsPath: configEcho.PATH ?? '',
                        forceTLS: (configEcho.SCHEME ?? 'https') === 'https',
                        authEndpoint: `${config.API_URL}/broadcasting/auth`, // '/broadcasting/auth',
                        // userAuthentication: {
                        //     endpoint: '/broadcasting/user-auth',
                        //     headers: {},
                        // },
                        csrfToken: null,
                        bearerToken: null,
                        encrypted: true,
                        disableStats: true,
                        enabledTransports: ['ws', 'wss'],
                        disabledTransports: ['sockjs', 'xhr_polling', 'xhr_streaming'],
                        auth: {
                            headers: {
                                Authorization: null,
                            }
                        },
                        //csrfToken: null,
                        //namespace: 'App',
                    });

                    this.broadcasting.echo.connector.pusher.connection.bind("state_change", (state) => {
                        switch (state.current) {
                            case "connected":
                                this.bindChannels();
                                break;
                            case "error":
                                break;
                            case "unavailable":
                            case "failed":
                            case "disconnected":
                                this.disconnectChannels();
                                break;
                        }
                    });
                }
                this.setAuthorization();
                this.broadcasting.echo.connect();

            } catch (err) {
                if(this.$root.$env.DEBUG){
                    console.error(error);
                }
                console.error('Unable to activate broadcast');
            }
        },
        setAuthorization() {
            let echo = this.broadcasting.echo;
            if(echo) {
                let token = this.authorizationToken;
                echo.options.bearerToken =  token;
                echo.connector.options.auth.headers.Authorization = token ? `Bearer ${token}` : null;
            }
        },
        /** Disconnect Echo **/
        disconnect(key='all'){
            let echo = this.broadcasting.echo;
            if(!echo) return;

            this.disconnectChannels(key);
            if(key == 'all') {
                // echo.connector.pusher.disconnect();
                echo.disconnect();
                this.broadcasting.echo = null;
            }
        },
        /** Disconnect Channels **/
        disconnectChannels(key='all'){
            let echo = this.broadcasting.echo;
            if(!echo) return;

            // echo.leaveAllChannels();
            if(key == 'all' || key == 'private') {
                this.broadcasting.subscribedChannels.private.forEach(channel => channel ? echo.leave(channel) : null);
                this.broadcasting.subscribedChannels.private.splice(0);
            }
            if(key == 'all' || key == 'public') {
                this.broadcasting.subscribedChannels.public.forEach(channel => channel ? echo.leave(channel) : null);
                this.broadcasting.subscribedChannels.public.splice(0);
            }
        },
        /** Bind Channels **/
        bindChannels(key='all'){
            let echo = this.broadcasting.echo;
            if(!echo) return;

            this.setAuthorization();

            if(key == 'all') {
                this.disconnectChannels();
            }

            let subscribedChannels = this.broadcasting.subscribedChannels;
            let channels = this.channels;
            if(channels) {
                Object.entries(channels).forEach(([key, events]) => {
                    let channelName = key;
                    let channel = null;
                    if(key.startsWith('private:')) {
                        channelName = key.replace('private:', '');
                        channel = this.currentUserId && !subscribedChannels.private.includes(channelName) ? echo.private(channelName) : null;
                    } else if(key.startsWith('presence:')) {
                        channelName = key.replace('presence:', '');
                        channel = this.currentUserId && !subscribedChannels.private.includes(channelName) ? echo.join(channelName) : null;
                    } else if(!subscribedChannels.public.includes(channelName)) {
                        channel = echo.channel(channelName);
                    }

                    if(channel && events) {
                        Object.keys(events).forEach(function(key){
                            // Bind the VM as second parameter
                            channel.listen(key, (payload) => {
                                events[key].bind(this)(payload);
                            });
                        }, this);

                        if(channelName != key) {
                            this.broadcasting.subscribedChannels.private.push(channelName);
                        }else{
                            this.broadcasting.subscribedChannels.public.push(channelName);
                        }
                    }
                });
            }

            // let vm = this;
            // this.echo.private('App.User' + '.' + this.currentUser.id)
            //     .notification((object) => vm.$store.commit('addNotification', object));
            // for local data use
            // this.echo.private('App.User' + '.' + this.currentUser.id)
            //     .notification((object) => vm.notifications.push(object))
        },
    }
}